import { useEffect, useRef } from 'react';

/**
 * Custom hook for monitoring performance metrics
 * Tracks page load times, API response times, and user interactions
 */
export const usePerformanceMonitor = (pageName: string) => {
  const startTime = useRef<number>(Date.now());
  const apiCalls = useRef<number>(0);
  const apiStartTimes = useRef<Map<string, number>>(new Map());

  // Track page load time
  useEffect(() => {
    const loadTime = Date.now() - startTime.current;
    console.log(`🚀 ${pageName} loaded in ${loadTime}ms`);

    // Send performance data to analytics (if available)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_load_time', {
        page_name: pageName,
        load_time: loadTime,
      });
    }
  }, [pageName]);

  // Track API call performance
  const trackApiCall = (apiName: string, startTime: number) => {
    apiCalls.current += 1;
    apiStartTimes.current.set(apiName, startTime);
  };

  const trackApiResponse = (apiName: string, success: boolean) => {
    const startTime = apiStartTimes.current.get(apiName);
    if (startTime) {
      const responseTime = Date.now() - startTime;
      console.log(
        `📡 ${apiName} ${success ? 'success' : 'failed'} in ${responseTime}ms`
      );

      // Send API performance data to analytics
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'api_call', {
          api_name: apiName,
          response_time: responseTime,
          success: success,
        });
      }

      apiStartTimes.current.delete(apiName);
    }
  };

  // Track user interactions
  const trackUserInteraction = (
    action: string,
    details?: Record<string, any>
  ) => {
    console.log(`👆 User ${action}`, details);

    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'user_interaction', {
        action: action,
        page: pageName,
        ...details,
      });
    }
  };

  return {
    trackApiCall,
    trackApiResponse,
    trackUserInteraction,
    apiCallsCount: apiCalls.current,
  };
};



